import { useCallback, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import debounce from 'lodash.debounce';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { useAuth0User } from '@hooks/useAuth0User';
import { hasSufficientPrivileges, isPaidMember } from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import useWindowSize from '@hooks/useWindowSize';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePlayers } from '@hooks/usePlayers';
import { useAccessToken } from '@hooks/useAccessToken';
import Button from '@components/atoms/button';
import CostSlider from '@components/fpl-ui/cost-slider';
import GameweekRangeSlider from '@components/fpl-ui/gameweek-range-slider';
import { SliderPresetButton } from '@components/fpl-ui/range-slider-field';
import PlayerPredictedPointsCard from '@components/predicted-points-card';
import Select from '@components/atoms/Select';
import Title from '@components/title';
import { MEMBERSHIPS, PAID_MEMBERSHIP_LEVELS } from '@lib/constants';
import { BodySmallRegularBold, H1, H3 } from '@components/atoms/typography';
import LoadingView from '@components/atoms/loading-view';
import SearchInput from '@components/SearchInput';
import AnimateSpinner from '@components/animate-spinner';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import type {
  PlayerPosition,
  PlayerSortBy,
  PlayerSortDirection
} from '@api-service/players';
import { getPageSeoHead, PAGE_SEO } from '@lib/page-seo';

const FFHModal = dynamic(() => import('@components/modal'));
const Paywall = dynamic(() => import('@components/paywall'));

type PositionOption = { value: PlayerPosition | 'ALL'; label: string };

const POSITION_OPTIONS: PositionOption[] = [
  { value: 'ALL', label: 'All' },
  { value: 'GK', label: 'GK' },
  { value: 'DEF', label: 'DEF' },
  { value: 'MID', label: 'MID' },
  { value: 'FWD', label: 'FWD' }
];

type SortOption = {
  value: string;
  label: string;
  sortBy: PlayerSortBy;
  sortDirection: PlayerSortDirection;
};

const SORT_OPTIONS: SortOption[] = [
  {
    value: 'predicted-points',
    label: 'Predicted points',
    sortBy: 'predictedPoints',
    sortDirection: 'desc'
  },
  {
    value: 'points-per-start',
    label: 'Points per start',
    sortBy: 'pointsPerStart',
    sortDirection: 'desc'
  },
  {
    value: 'ownership-desc',
    label: 'Ownership (high to low)',
    sortBy: 'ownership',
    sortDirection: 'desc'
  },
  {
    value: 'ownership-asc',
    label: 'Ownership (low to high)',
    sortBy: 'ownership',
    sortDirection: 'asc'
  },
  {
    value: 'price-desc',
    label: 'Price (high to low)',
    sortBy: 'price',
    sortDirection: 'desc'
  },
  {
    value: 'price-asc',
    label: 'Price (low to high)',
    sortBy: 'price',
    sortDirection: 'asc'
  }
];

const DESKTOP_GW_HORIZON = 7;
const MOBILE_GW_HORIZON = 3;
const MAX_GAMEWEEK = 38;
const PAGE_SIZE = 20;

const getGameweekRange = (
  nextGameweek: number,
  isDesktop: boolean
): [number, number] => {
  const horizon = isDesktop ? DESKTOP_GW_HORIZON : MOBILE_GW_HORIZON;
  return [nextGameweek, Math.min(MAX_GAMEWEEK, nextGameweek + horizon)];
};

const PredictionsPage = () => {
  const router = useRouter();
  const pathname = router.pathname;
  const pageHead = getPageSeoHead(pathname, PAGE_SEO.predictions);

  const { user, isLoading: userIsLoading } = useAuth0User();
  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const isAuthorisedToViewRestrictedPageContent =
    hasPaidMembership && hasSufficientMembershipLevel;

  const { accessToken } = useAccessToken();
  const { context: leagueContext, isLoading: contextIsLoading } =
    useLeagueContext();

  const { width } = useWindowSize();
  const isMobile = width < 1020;

  const currentGameweekNumber = leagueContext?.currentGameweek?.number ?? null;
  const nextGameweekNumber = leagueContext?.nextGameweek?.number ?? null;
  const priceRange = leagueContext?.playerPriceRange ?? null;

  const [positionFilter, setPositionFilter] = useState<PlayerPosition | 'ALL'>(
    'ALL'
  );
  const [gameweekRange, setGameweekRange] = useState<[number, number] | null>(
    null
  );
  const [costRange, setCostRange] = useState<[number, number] | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState<string>(SORT_OPTIONS[0].value);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (nextGameweekNumber && !gameweekRange) {
      setGameweekRange(getGameweekRange(nextGameweekNumber, !isMobile));
    }
  }, [nextGameweekNumber, isMobile, gameweekRange]);

  // Predictions now require an authenticated call server-side, so there is
  // nothing for non-members to browse: land them on the paywall straight away.
  useEffect(() => {
    if (!userIsLoading && !isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
    }
  }, [userIsLoading, isAuthorisedToViewRestrictedPageContent]);

  useEffect(() => {
    if (priceRange && !costRange) {
      setCostRange([priceRange.min, priceRange.max]);
    }
  }, [priceRange, costRange]);

  const debouncedSetSearch = useCallback(
    debounce((nextValue: string) => {
      setDebouncedSearch(nextValue);
    }, 500),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const selectedSort = useMemo(
    () =>
      SORT_OPTIONS.find((option) => option.value === sortValue) ??
      SORT_OPTIONS[0],
    [sortValue]
  );

  // Members need their token attached before the query fires (an anonymous
  // request with predictions is rejected server-side), so hold the query while
  // the user state or token is still resolving.
  const isAwaitingAccessToken =
    userIsLoading || (isAuthorisedToViewRestrictedPageContent && !accessToken);
  const queryEnabled =
    !isAwaitingAccessToken &&
    Boolean(nextGameweekNumber) &&
    Boolean(gameweekRange) &&
    Boolean(costRange);

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePlayers({
      enabled: queryEnabled,
      accessToken: isAuthorisedToViewRestrictedPageContent
        ? accessToken ?? undefined
        : undefined,
      // Non-members fetch the raw player list without predictions (the API
      // rejects anonymous prediction reads and prediction-based sorts), so the
      // table still renders behind the paywall modal.
      exclude: isAuthorisedToViewRestrictedPageContent
        ? undefined
        : 'predictions',
      limit: PAGE_SIZE,
      position: positionFilter === 'ALL' ? undefined : positionFilter,
      minGameweek: gameweekRange?.[0],
      maxGameweek: gameweekRange?.[1],
      minPrice: costRange?.[0],
      maxPrice: costRange?.[1],
      search: debouncedSearch || undefined,
      sortBy: isAuthorisedToViewRestrictedPageContent
        ? selectedSort.sortBy
        : undefined,
      sortDirection: isAuthorisedToViewRestrictedPageContent
        ? selectedSort.sortDirection
        : undefined
    });

  const players = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );

  const fplSeasonIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.fplSeasonIsClosed,
    false
  );

  const handleSearchChange = (event) => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    const { value } = event.target;
    setSearchInput(value);
    debouncedSetSearch.cancel();
    debouncedSetSearch(value);
  };

  const handlePositionChange = (option: PositionOption) => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    setPositionFilter(option.value);
  };

  const handleSortChange = (option: { value: string }) => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    setSortValue(option.value);
  };

  const handleGameweekChange = (range: [number, number]) => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    setGameweekRange(range);
  };

  const handleCostChange = (range: [number, number]) => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    setCostRange(range);
  };

  const handleLoadMore = () => {
    if (!isAuthorisedToViewRestrictedPageContent) {
      setOpenModal(true);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const { isAppView } = useIsAppView();

  if (currentGameweekNumber === MAX_GAMEWEEK || fplSeasonIsClosed) {
    return (
      <>
        <Head>{pageHead}</Head>
        <Title>Predicted Points</Title>
        <EndOfSeasonHoldingMessage
          pageTitle="Points predictions"
          isAppView={isAppView}
        />
      </>
    );
  }

  const isInitialLoad =
    userIsLoading ||
    contextIsLoading ||
    (queryEnabled && isFetching && players.length === 0);

  const sliderMaxGameweek = nextGameweekNumber
    ? Math.min(MAX_GAMEWEEK, nextGameweekNumber + DESKTOP_GW_HORIZON)
    : MAX_GAMEWEEK;

  return (
    <>
      <Head>{pageHead}</Head>
      <Title>Predicted Points</Title>
      <div className="bg-white mx-auto max-w-7xl pb-14 relative px-2 pt-2 lg:pt-8 pb-20 bg-gray-50 sm:px-6 lg:pb-10 lg:px-8">
        <H1 className="py-3 px-2 lg:px-0 lg:hidden">Predicted Points</H1>
        <div className="shadow-100 rounded-lg px-2 px-2 md:py-4 md:px-5">
          <H3>Filters</H3>
          <div className="flex flex-col xl:flex-row justify-between space-y-3 xl:space-y-0 items-start xl:items-end">
            <div
              className={`${
                isMobile ? 'w-full' : ''
              } flex flex-col items-start md:items-end space-y-3 md:flex-row md:space-y-0 w-full`}
            >
              <div className="mt-3 w-[250px] mr-2">
                <SearchInput
                  id="name"
                  type="text"
                  label="Search"
                  placeholder="Search by player or team"
                  handleChange={handleSearchChange}
                  value={searchInput}
                  showSearchIcon
                  showCloseIcon={false}
                  onClose={() => {
                    setSearchInput('');
                    debouncedSetSearch.cancel();
                    setDebouncedSearch('');
                  }}
                />
              </div>
              <div className="flex justify-between pb-1 py-2 md:py-0">
                {POSITION_OPTIONS.map((option, index) => (
                  <BodySmallRegularBold
                    data-cy={`player-position-select-${index}`}
                    onClick={() => handlePositionChange(option)}
                    key={option.value}
                    className="flex-1 text-center py-2 px-4 md:px-6 rounded-lg cursor-pointer transition mr-2"
                    textColor={
                      positionFilter === option.value
                        ? 'text-neutral-50 bg-primary-600'
                        : 'text-success-800 bg-neutral-100'
                    }
                  >
                    {option.label}
                  </BodySmallRegularBold>
                ))}
              </div>
            </div>
            {gameweekRange && costRange && nextGameweekNumber ? (
              <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 w-full md:w-max">
                <div className="w-full px-3 md:w-[300px] mr-3">
                  <GameweekRangeSlider
                    name="gameweek-slider"
                    label="GW range"
                    min={nextGameweekNumber}
                    max={sliderMaxGameweek}
                    value={gameweekRange}
                    onCommit={handleGameweekChange}
                    actions={
                      <SliderPresetButton
                        isActive={
                          nextGameweekNumber === gameweekRange[0] &&
                          nextGameweekNumber === gameweekRange[1]
                        }
                        onClick={() =>
                          handleGameweekChange([
                            nextGameweekNumber,
                            nextGameweekNumber
                          ])
                        }
                      >
                        Next GW
                      </SliderPresetButton>
                    }
                  />
                </div>
                <div className="w-full px-3 md:w-[300px]">
                  <CostSlider
                    defaultMin={priceRange?.min ?? costRange[0]}
                    defaultMax={priceRange?.max ?? costRange[1]}
                    update={handleCostChange}
                    label="Price"
                  />
                </div>
              </div>
            ) : (
              <AnimateSpinner />
            )}
          </div>
        </div>
        <div>
          {isInitialLoad && (
            <div className="h-[85vh] pt-5">
              <LoadingView
                headerText="Points Predictions"
                loadingText="AI generating predicted points..."
                numberOfSeconds="8"
                progressBarSeconds={8}
              />
            </div>
          )}

          {!isInitialLoad && (
            <>
              <FFHModal
                open={openModal}
                handleClose={() => setOpenModal(false)}
              >
                <Paywall
                  variant="modal"
                  title="Unlock the prediction tool 🔒"
                />
              </FFHModal>
              <div className="relative">
                <div className={`mt-5 ${isFetching && 'opacity-50'}`}>
                  <div className="flex flex-col md:w-[260px] mt-5 mb-3">
                    <Select
                      name="select-prediction"
                      dataCy="prediction-sort"
                      onChange={handleSortChange}
                      label="Sort by"
                      options={SORT_OPTIONS.map(({ value, label }) => ({
                        value,
                        label
                      }))}
                      value={
                        SORT_OPTIONS.map(({ value, label }) => ({
                          value,
                          label
                        })).find((option) => option.value === sortValue) ?? null
                      }
                    />
                  </div>
                  {players.length === 0 ? (
                    <div className="text-center py-10">
                      <BodySmallRegularBold>
                        No players match the current filters
                      </BodySmallRegularBold>
                    </div>
                  ) : (
                    <>
                      {players.map((player) => (
                        <PlayerPredictedPointsCard
                          key={player.id}
                          player={player}
                          isMobile={isMobile}
                          isAuthorisedToViewRestrictedPageContent={
                            isAuthorisedToViewRestrictedPageContent
                          }
                        />
                      ))}
                      {hasNextPage && (
                        <div className="flex justify-center mx-auto my-5">
                          <Button
                            onClick={handleLoadMore}
                            variant="primary"
                            className="mx-auto"
                            disabled={isFetchingNextPage}
                          >
                            {isFetchingNextPage ? 'Loading...' : 'Load more'}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PredictionsPage;
